import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../lib/apiError';
import { formatMonthName } from '../../lib/formatDate';
import { ErrorState } from '../ErrorState';
import { Select } from '../inputs/Select';
import { Tooltip } from '../Tooltip';
import { PeriodStep } from './PeriodStep';
import { todayCalendarDate } from './monthGrid';

/** How far either side of this year the picker reaches. A permit renewed every five years fits. */
const YEARS_EITHER_SIDE = 10;

const MONTH_NUMBERS = Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, '0'));

/** One row of a month column — what the caller's record is, already in words. */
export interface YearCalendarEntry {
  /** Unique within the year — a record may fall on several dates. */
  key: string;
  /** `YYYY-MM-DD`, a CALENDAR date. */
  date: string;
  /** The marker's background class. A colour is never the only signal — `detail` names the state. */
  dot: string;
  title: string;
  /** The whole of it, for the tooltip — the reference, the title, the state. */
  tooltip: string;
  /** Read after the title by a screen reader — the reference and the state, comma-led. */
  detail: string;
  onOpen: () => void;
}

/**
 * An index page's CALENDAR view — one year as twelve month columns, each listing every entry
 * that falls in that month, one row per date. Building Management's register and Press Con both
 * draw it through `<IndexPage extraView>`.
 *
 * **It is the register's own list, laid out by date.** It sits under the index page's toolbar,
 * tiles, tabs and rail, and the caller's `load` is fetched with the same search and facets — so
 * ticking a facet shows those records' dates and nothing else.
 *
 * **`load` must be stable** (`useCallback` keyed on what its filters SAY, never on their
 * identity) — the shell hands a new filter object every render, and a fresh `load` refetches the
 * year.
 */
export function YearCalendar({
  id,
  load,
  reloadKey,
  summary,
}: {
  /** The DOM id prefix — the year picker is `${id}-year`, a month heading `${id}-MM`. */
  id: string;
  load: (from: string, to: string, signal: AbortSignal) => Promise<YearCalendarEntry[]>;
  reloadKey: number;
  /** The sentence beside the year — `3 due dates in 2026.` */
  summary: (count: number, year: number) => string;
}) {
  const thisYear = Number(todayCalendarDate().slice(0, 4));
  const [year, setYear] = useState(thisYear);
  const [entries, setEntries] = useState<YearCalendarEntry[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    // The previous answer is dropped first, so a slow one never paints the wrong year.
    setEntries(null);
    setError(null);

    load(`${year}-01-01`, `${year}-12-31`, controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setEntries(next);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(caught instanceof ApiError ? caught : new ApiError('Could not load the calendar.', 0));
      });

    return () => controller.abort();
  }, [year, load, reloadKey, retryKey]);

  // The caller answers in date order, so each month's rows arrive already sorted.
  const byMonth = useMemo(() => {
    const map = new Map<string, YearCalendarEntry[]>();

    for (const entry of entries ?? []) {
      const month = entry.date.slice(5, 7);
      const list = map.get(month) ?? [];
      list.push(entry);
      map.set(month, list);
    }

    return map;
  }, [entries]);

  const years = useMemo(
    () => Array.from({ length: YEARS_EITHER_SIDE * 2 + 1 }, (_, index) => thisYear - YEARS_EITHER_SIDE + index),
    [thisYear],
  );

  const count = entries?.length ?? 0;
  const currentMonth = todayCalendarDate().slice(0, 7);

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-2.5">
        <div className="flex items-center gap-3">
          <div className="w-28">
            <Select
              id={`${id}-year`}
              ariaLabel="Year"
              compact
              clearable={false}
              sort="none"
              value={String(year)}
              onChange={(next) => setYear(Number(next))}
              options={years.map((value) => ({ value: String(value), label: String(value) }))}
            />
          </div>

          <PeriodStep
            previousLabel="Previous year"
            nextLabel="Next year"
            onPrevious={() => setYear((value) => value - 1)}
            onNext={() => setYear((value) => value + 1)}
          />

          <p className="text-xs text-slate-500" aria-live="polite">
            {entries === null && !error ? `Loading ${year}…` : summary(count, year)}
          </p>
        </div>

        {/* No legend here: the page's tiles above carry the same dots and words. */}
      </div>

      {error ? (
        <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => setRetryKey((k) => k + 1)} />
      ) : (
        /*
          THE ONE SCROLL REGION, and the innermost: twelve columns do not fit a laptop, so the
          grid scrolls sideways under the year bar instead of the bar scrolling away with it.
        */
        <div className="modern-scrollbar min-h-0 flex-1 overflow-auto">
          <div className="grid min-h-full grid-cols-[repeat(12,minmax(13rem,1fr))]">
            {MONTH_NUMBERS.map((monthNumber, index) => {
              const month = `${year}-${monthNumber}`;
              const rows = byMonth.get(monthNumber) ?? [];
              const isCurrent = month === currentMonth;

              return (
                <section
                  key={monthNumber}
                  aria-labelledby={`${id}-${monthNumber}`}
                  className={`flex min-w-0 flex-col ${index === 0 ? '' : 'border-l border-slate-200'}`}
                >
                  <h3
                    id={`${id}-${monthNumber}`}
                    className={`sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-slate-200 px-3 py-2 text-[9px] uppercase tracking-wider ${
                      isCurrent ? 'bg-blue-50 text-slate-900' : 'bg-slate-100 text-gray-600'
                    }`}
                  >
                    <span>
                      {formatMonthName(month)}
                      {isCurrent ? <span className="sr-only"> (this month)</span> : null}
                    </span>
                    {entries !== null ? (
                      <span className={`font-mono tabular-nums ${rows.length === 0 ? 'text-slate-300' : ''}`}>
                        {rows.length}
                      </span>
                    ) : null}
                  </h3>

                  {rows.length > 0 ? (
                    <ul className="flex flex-col">
                      {rows.map((entry) => (
                        <CalendarRow key={entry.key} entry={entry} />
                      ))}
                    </ul>
                  ) : null}
                </section>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/** One date in a month column — the dot, the day, and what the record is. */
function CalendarRow({ entry }: { entry: YearCalendarEntry }) {
  return (
    <li className="min-w-0 border-b border-slate-200">
      <Tooltip label={entry.tooltip} block>
        <button
          type="button"
          onClick={entry.onOpen}
          /*
            A CALENDAR CELL'S VALUE IS NEVER THE DARK slate-800/900 (product call, 12 Oct 2026) —
            slate-500 at regular weight, 11px, like a table cell. Hover underlines it and the
            cursor is the hand, so it still reads as clickable. The dot leads, then the day.
          */
          className="flex w-full min-w-0 items-center gap-2 px-3 py-1.5 text-left text-[11px] text-slate-500 hover:bg-slate-50 hover:underline"
        >
          <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${entry.dot}`} />
          <span className="shrink-0 font-mono tabular-nums text-slate-400">{entry.date.slice(8, 10)}</span>
          <span className="truncate">{entry.title}</span>
          <span className="sr-only">{entry.detail}</span>
        </button>
      </Tooltip>
    </li>
  );
}
